import React from "react";
import aivimartLogo from "../../assets/logo2.png";

const ComponentToPrint = React.forwardRef(({ orderData, orderDetails }, ref) => {
  if (!orderData) return null;

  const items = orderDetails || [];
  const customerTotal = Number(orderData.overall_price || 0);
  const itemsSubtotal = Number(orderData.total_price || 0);
  const shippingCharge = Number(orderData.shipping_charge || 0);
  const isOnline = orderData.payment_type === "Online" || orderData.payment_type == 1;

  const shipping = orderData.shippingAddress || {};
  const billing = orderData.billingAddress || shipping;

  const formatAddr = (addr) => {
    return [addr.address, addr.street, addr.land_mark, addr.city, addr.state].filter(Boolean).join(", ");
  };

  return (
    <div ref={ref} style={styles.page}>
      {/* ── Invoice Header ── */}
      <div style={styles.headerRow}>
        <div>
          <img src={aivimartLogo} alt="Aivimart" style={styles.logo} />
          <p style={styles.companyMeta}>
            Email: Aivimart8@gmail.com<br />
            Website: https://aivimart.com
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <h3 style={styles.invoiceTitle}>TAX INVOICE / RECEIPT</h3>
          <table style={styles.metaTable}>
            <tbody>
              <tr>
                <td style={styles.metaLabel}>Invoice / Order #:</td>
                <td style={styles.metaValue}>{orderData.order_uuid}</td>
              </tr>
              <tr>
                <td style={styles.metaLabel}>Order Date:</td>
                <td style={styles.metaValue}>{orderData.order_date || "—"}</td>
              </tr>
              <tr>
                <td style={styles.metaLabel}>Payment Mode:</td>
                <td style={styles.metaValue}>{isOnline ? "Online (Prepaid)" : "Cash on Delivery"}</td>
              </tr>
              <tr>
                <td style={styles.metaLabel}>Payment Status:</td>
                <td style={styles.metaValue}>{orderData.payment_status == 2 ? "PAID" : "PENDING"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <hr style={styles.divider} />

      {/* ── Customer Addresses ── */}
      <div style={styles.addressRow}>
        <div style={styles.addressCol}>
          <div style={styles.addressHeader}>BILLED TO:</div>
          <div style={styles.customerName}>{billing.user_name || orderData.user_name || "Customer"}</div>
          <div style={styles.addressText}>
            {formatAddr(billing)}
            {billing.pin ? ` - ${billing.pin}` : ""}
          </div>
          {billing.mobile && <div style={styles.phoneText}>Mobile: {billing.mobile}</div>}
          {orderData.email && <div style={styles.phoneText}>Email: {orderData.email}</div>}
        </div>

        <div style={styles.addressCol}>
          <div style={styles.addressHeader}>SHIPPED TO:</div>
          <div style={styles.customerName}>{shipping.user_name || orderData.user_name || "Customer"}</div>
          <div style={styles.addressText}>
            {formatAddr(shipping)}
            {shipping.pin ? ` - ${shipping.pin}` : ""}
          </div>
          {shipping.mobile && <div style={styles.phoneText}>Mobile: {shipping.mobile}</div>}
        </div>
      </div>

      {/* ── Items Table ── */}
      <table style={styles.itemsTable}>
        <thead>
          <tr style={styles.tableHeaderRow}>
            <th style={{ ...styles.th, width: "35px" }}>#</th>
            <th style={styles.th}>Item Description</th>
            <th style={{ ...styles.th, textAlign: "center", width: "50px" }}>Qty</th>
            <th style={{ ...styles.th, textAlign: "right", width: "85px" }}>Unit Price</th>
            <th style={{ ...styles.th, textAlign: "right", width: "70px" }}>GST</th>
            <th style={{ ...styles.th, textAlign: "right", width: "95px" }}>Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const pPrice = Number(item.product_price || (item.price * item.quantity) || 0);
            const pWithGst = Number(item.product_price_with_gst || (item.price * item.quantity * 1.18) || 0);
            const gstRate = item.product_gst_rate || 18;
            const gstAmt = pWithGst - pPrice;
            const unitPrice = item.quantity > 0 ? (pPrice / item.quantity) : pPrice;

            return (
              <tr key={idx} style={idx % 2 === 1 ? styles.altRow : {}}>
                <td style={styles.td}>{idx + 1}</td>
                <td style={styles.td}>
                  <div style={styles.itemName}>{item.product_name}</div>
                  <div style={styles.itemMeta}>
                    {[item.brand && `Brand: ${item.brand}`, item.category && `Cat: ${item.category}`].filter(Boolean).join(" | ")}
                  </div>
                </td>
                <td style={{ ...styles.td, textAlign: "center" }}>{item.quantity}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>₹ {unitPrice.toFixed(2)}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                  ₹ {gstAmt.toFixed(2)}
                  <div style={{ fontSize: "10px", color: "#888" }}>({gstRate}%)</div>
                </td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>₹ {pWithGst.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Summary & Totals ── */}
      <div style={styles.summaryContainer}>
        <div style={styles.summaryNotes}>
          <div style={styles.notesTitle}>Important Notes:</div>
          <ul style={styles.notesList}>
            <li>All items are inspected prior to dispatch.</li>
            <li>This is a computer-generated invoice and does not require a physical signature.</li>
          </ul>
        </div>

        <table style={styles.summaryTable}>
          <tbody>
            <tr>
              <td style={styles.summaryLabel}>Items Subtotal (excl. tax):</td>
              <td style={styles.summaryVal}>
                ₹ {items.reduce((acc, i) => acc + Number(i.product_price || (i.price * i.quantity) || 0), 0).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style={styles.summaryLabel}>Total GST / Taxes:</td>
              <td style={styles.summaryVal}>
                ₹ {items.reduce((acc, i) => {
                  const pPrice = Number(i.product_price || (i.price * i.quantity) || 0);
                  const pWithGst = Number(i.product_price_with_gst || (i.price * i.quantity * 1.18) || 0);
                  return acc + (pWithGst - pPrice);
                }, 0).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style={styles.summaryLabel}>Items Total (incl. GST):</td>
              <td style={styles.summaryVal}>
                ₹ {items.reduce((acc, i) => acc + Number(i.product_price_with_gst || (i.price * i.quantity * 1.18) || 0), 0).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style={styles.summaryLabel}>Shipping Charges:</td>
              <td style={styles.summaryVal}>₹ {shippingCharge.toFixed(2)}</td>
            </tr>
            <tr style={styles.grandTotalRow}>
              <td style={styles.grandTotalLabel}>GRAND TOTAL:</td>
              <td style={styles.grandTotalVal}>₹ {customerTotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div style={styles.footer}>
        <p style={{ margin: 0, fontWeight: "600", color: "#111" }}>Thank you for shopping with Aivimart!</p>
        <p style={{ margin: "2px 0 0 0", color: "#777", fontSize: "11px" }}>
          For queries or help, reach us at Aivimart8@gmail.com
        </p>
      </div>
    </div>
  );
});

export default ComponentToPrint;

const styles = {
  page: {
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    padding: "18px 24px",
    color: "#222",
    backgroundColor: "#fff",
    maxWidth: "800px",
    margin: "0 auto",
    fontSize: "11.5px",
    lineHeight: 1.3,
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  logo: {
    height: "38px",
    width: "auto",
    maxWidth: "160px",
    objectFit: "contain",
    display: "block",
    marginBottom: "4px",
  },
  brandTitle: {
    margin: "0 0 2px 0",
    fontSize: "22px",
    fontWeight: "800",
    letterSpacing: "1px",
    color: "#c62828",
  },
  brandSubtitle: {
    margin: "0 0 4px 0",
    fontSize: "11px",
    fontWeight: "600",
    color: "#666",
    textTransform: "uppercase",
  },
  companyMeta: {
    margin: 0,
    fontSize: "10.5px",
    color: "#666",
    lineHeight: 1.25,
  },
  invoiceTitle: {
    margin: "0 0 6px 0",
    fontSize: "15px",
    fontWeight: "700",
    color: "#333",
    letterSpacing: "0.5px",
  },
  metaTable: {
    fontSize: "10.5px",
    borderCollapse: "collapse",
    marginLeft: "auto",
  },
  metaLabel: {
    textAlign: "right",
    paddingRight: "8px",
    color: "#666",
    fontWeight: "500",
  },
  metaValue: {
    textAlign: "right",
    fontWeight: "700",
    color: "#111",
  },
  divider: {
    border: "none",
    borderTop: "2px solid #e0e0e0",
    margin: "8px 0 10px 0",
  },
  addressRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    marginBottom: "12px",
    backgroundColor: "#fafafa",
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #eee",
  },
  addressCol: {
    flex: 1,
  },
  addressHeader: {
    fontSize: "10px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#888",
    marginBottom: "4px",
  },
  customerName: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#111",
    marginBottom: "2px",
  },
  addressText: {
    fontSize: "11px",
    color: "#444",
    lineHeight: 1.35,
    marginBottom: "3px",
  },
  phoneText: {
    fontSize: "11px",
    color: "#555",
  },
  itemsTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px",
  },
  tableHeaderRow: {
    backgroundColor: "#f5f5f5",
    borderTop: "2px solid #ccc",
    borderBottom: "2px solid #ccc",
  },
  th: {
    padding: "8px 10px",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
    color: "#444",
    textAlign: "left",
  },
  td: {
    padding: "8px 10px",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
  },
  altRow: {
    backgroundColor: "#fafafa",
  },
  itemName: {
    fontWeight: "600",
    color: "#111",
  },
  itemMeta: {
    fontSize: "10px",
    color: "#777",
    marginTop: "2px",
  },
  summaryContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    marginBottom: "24px",
  },
  summaryNotes: {
    flex: 1,
    paddingRight: "20px",
  },
  notesTitle: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#555",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  notesList: {
    margin: 0,
    paddingLeft: "16px",
    fontSize: "10px",
    color: "#777",
    lineHeight: 1.4,
  },
  summaryTable: {
    width: "280px",
    borderCollapse: "collapse",
  },
  summaryLabel: {
    padding: "4px 8px",
    fontSize: "11px",
    color: "#555",
    textAlign: "left",
  },
  summaryVal: {
    padding: "4px 8px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#111",
    textAlign: "right",
  },
  grandTotalRow: {
    borderTop: "2px solid #333",
    borderBottom: "2px solid #333",
    backgroundColor: "#f9f9f9",
  },
  grandTotalLabel: {
    padding: "8px",
    fontSize: "13px",
    fontWeight: "800",
    color: "#111",
  },
  grandTotalVal: {
    padding: "8px",
    fontSize: "15px",
    fontWeight: "800",
    color: "#1a6b3c",
    textAlign: "right",
  },
  footer: {
    textAlign: "center",
    paddingTop: "16px",
    borderTop: "1px dashed #ccc",
  },
};

